/**
 * Launch a created payment intent with whatever gateway the hotel runs.
 * Hosted-checkout gateways (Stripe et al.) come back via redirect_url; the
 * gateway returns the guest to `<origin><returnPath>?payment_intent_id=…&status=…`.
 * Razorpay opens its inline modal and we synthesize the same return URL so
 * both paths converge on one confirm handler.
 */
export interface GatewayIntent {
  payment_intent_id?: string;
  redirect_url?: string;
  gateway?: string;
  amount?: number;
  currency?: string;
  key_id?: string;
  gateway_config?: { razorpay_key_id?: string };
}

export function launchGatewayIntent(
  intent: GatewayIntent,
  opts: {
    returnPath: string;
    hotelName: string;
    description: string;
    onDismiss: () => void;
    onError: (message: string) => void;
  }
): void {
  if (intent.redirect_url) {
    window.location.href = intent.redirect_url;
    return;
  }
  if (intent.gateway === 'razorpay' && intent.payment_intent_id) {
    const launch = () => {
      const rzp = new (window as any).Razorpay({
        key: intent.key_id || intent.gateway_config?.razorpay_key_id,
        amount: Math.round((intent.amount || 0) * 100),
        currency: intent.currency,
        name: opts.hotelName,
        description: opts.description,
        order_id: intent.payment_intent_id,
        handler: () => {
          window.location.href =
            `${window.location.origin}${opts.returnPath}` +
            `?payment_intent_id=${intent.payment_intent_id}&status=success`;
        },
        modal: { ondismiss: opts.onDismiss },
        theme: { color: '#1A56DB' },
      });
      rzp.open();
    };
    if ((window as any).Razorpay) {
      launch();
    } else {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = launch;
      script.onerror = () => opts.onError('gateway_unavailable');
      document.body.appendChild(script);
    }
    return;
  }
  opts.onError('gateway_unavailable');
}
